'use client';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faGhost, faInfoCircle, faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import moment from 'moment';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { Session } from '@/lib/getSessionPages';
import DetailsNav from '@/modules/league/details/components/DetailsNav';
import { RegistrationButton } from '@/modules/league/details/components/RegistrationButton';
import RegistrationInfoBox from '@/modules/league/details/components/RegistrationInfoBox';
import { useClubMemberStatus } from '@/modules/league/hooks/useClubMemberStatus';
import { useRegistrationStatus } from '@/modules/league/hooks/useRegistrationStatus';
import {
  LeagueDetails,
  LocationLeagueDetails,
  SessionLeagueDetails,
} from '@/modules/league/new-components/types';

interface DetailsHeaderProps {
  details: LeagueDetails;
  session: SessionLeagueDetails;
  gameDay: string;
  location: LocationLeagueDetails;
  isRegistered: boolean;
  params: { slug: string; sessionId: string };
  showSponsors: boolean;
  user?: Session;
}

const InfoBox = ({ icon, children }: { icon?: IconProp; children: React.ReactNode }) => (
  <div className='w-full rounded-lg bg-gray-100 p-4 text-gray-700'>
    {icon ? <FontAwesomeIcon className='mr-2' icon={icon} /> : null}
    {children}
  </div>
);

export default function DetailsHeader({
  details,
  session,
  gameDay,
  isRegistered,
  location,
  params,
  showSponsors,
  user,
}: DetailsHeaderProps) {
  const isLocked = details.IsLocked === true;
  const isPartnerLeague = details.PlayerTypeID === 2;
  const isLiveSession = session.IsLiveSession;

  const { slug } = params;

  const isUserLoggedIn = user?.token && user?.pbUuid ? true : false;

  const userTimeInSpecifiedZone = moment().tz(session.Timezone_IanaID).utc();
  const registrationCloseDate = moment.parseZone(session.Registration_DateClosed_UTC);
  const isRegistrationDateAfterNow = userTimeInSpecifiedZone.isAfter(registrationCloseDate);
  const isRegistrationLocked = details.IsLocked;

  const isRegistrationOnlyForMembers = !session.AllowNonMemberSignUp;
  const firstPlayerMembershipStatus = session.ClubMemberStatus;

  const registrationButtonStatus = useRegistrationStatus(
    isRegistrationLocked,
    isRegistrationDateAfterNow,
    isRegistrationOnlyForMembers,
    session,
    isRegistered,
    isUserLoggedIn
  );

  const playerMembershipStatus = useClubMemberStatus(
    false,
    isRegistrationOnlyForMembers,
    firstPlayerMembershipStatus
  );

  const locationInfo = `${session.Location_Title} - ${location.Location_StreetAddress} ${location.Location_City}, ${location.StateTitle} ${location?.CountryTitle}`;
  const leagueFormatAndTypeInfo = `${details.PlayerGroupTitle} ${details.FormatTitle} ${details.LeagueTypeTitle} ${details.PlayerTypeTitle}`;
  const ageInfo = `Age: ${session.AgeRangeForUI}`;
  const skillInfo = `Skill: ${session.RankRangeForUI}`;

  return (
    <div className='flex flex-col gap-4 py-4'>
      <div className='flex flex-wrap gap-4 lg:flex-row'>
        <div className='flex flex-col-reverse sm:flex-col gap-4 w-full'>
          <div className='relative min-h-44 max-h-64 w-full justify-between bg-white sm:h-[22.5rem] sm:min-h-[22.5rem] sm:max-h-[23rem]'>
            <div className='relative size-full'>
              <ImageWithFallback
                src={details.ImageURL}
                fill
                priority
                className='object-contain'
                alt=''
                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
              />
            </div>
          </div>

          <h3
            className={`text-base sm:text-2xl font-semibold ${isLocked ? 'text-error-500' : 'text-gray-900'}`}
          >
            {isLocked ? <FontAwesomeIcon icon={faLock} className='mr-2' /> : null}
            {details.Title}
          </h3>
        </div>

        <div className='hidden sm:flex flex-col gap-4 w-full'>
          <InfoBox icon={faLocationDot}>{locationInfo}</InfoBox>
          <InfoBox>
            <div>{leagueFormatAndTypeInfo}</div>
            <div>{ageInfo}</div>
            <div>{skillInfo}</div>
          </InfoBox>
        </div>
      </div>

      <div className='flex-row text-sm flex'>
        <div className='flex w-full flex-col-reverse sm:flex-col gap-4'>
          <div className='flex sm:hidden flex-col gap-4 w-full'>
            <InfoBox icon={faLocationDot}>{locationInfo}</InfoBox>
            <InfoBox>
              <div>{leagueFormatAndTypeInfo}</div>
              <div>{ageInfo}</div>
              <div>{skillInfo}</div>
            </InfoBox>
          </div>
          <div className='block sm:hidden'>
            <RegistrationInfoBox
              details={details}
              session={session}
              isRegistered={isRegistered}
              params={params}
              user={user}
            />
          </div>

          <div className='flex flex-col gap-2 font-semibold'>
            <div className='flex flex-row'>
              <span> Viewing Session:</span>
              <span className='ml-4 font-normal'> {session.SessionNumber} </span>
            </div>
            {gameDay ? (
              <div className='flex flex-row'>
                <span> Next Game Day:</span>
                <span className='ml-6 font-normal'> {gameDay} </span>
              </div>
            ) : null}
          </div>

          {isLiveSession && session.MatchDateID_Cur ? (
            <div className='flex w-full justify-between lg:w-1/3 mt-0 sm:mt-4'>
              <CustomLink
                href={`${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${session.LeagueSessionID}/matchdate/${session.MatchDateID_Cur}?tab=rsvp`}
                className='flex w-full'
              >
                <Button
                  variant='secondary'
                  className='flex w-full justify-center rounded-md font-semibold text-gray-700'
                >
                  Current Game Day
                </Button>
              </CustomLink>
            </div>
          ) : null}

          <div className='block sm:hidden'>
            <RegistrationButton
              isUserLoggedIn={isUserLoggedIn}
              status={registrationButtonStatus}
              session={session}
              playerMembershipStatus={playerMembershipStatus}
            />
          </div>
        </div>
      </div>

      {isPartnerLeague ? (
        <div className='w-full rounded-lg border border-leagues-300/50 bg-primary-25 p-4 text-leagues-500'>
          <FontAwesomeIcon className='mr-2' icon={faInfoCircle} />
          Both partners MUST register independently. Players needing to register are identified with
          a ghost
          <FontAwesomeIcon icon={faGhost} className='mx-1 inline-block' /> next to their name.
          Incomplete teams will NOT be included in game days.
        </div>
      ) : null}

      <div className='hidden lg:flex'>
        <DetailsNav
          leagueId={session.LeagueID}
          sessionId={session.LeagueSessionID}
          params={params}
          showSponsors={showSponsors}
        />
      </div>
    </div>
  );
}
